import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { ticketTypeFormSchema, type TicketTypeFormValues } from './ticketTypeFormSchema'
import type { TicketRequestType, TicketRequestTypeUpsert } from '../types'

interface Props {
  visible: boolean
  initial: TicketRequestType | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: TicketRequestTypeUpsert) => void
  onHide: () => void
}

/** Talep türü ekle/düzenle formu (020: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function TicketTypeFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(ticketTypeFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: TicketTypeFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('admin.types.form.editTitle') : t('admin.types.form.newTitle')}
      submitLabel={t('admin.types.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField control={control} name="nameTr" label={t('admin.types.form.nameTr')} required autoFocus />
        <FormTextField control={control} name="nameEn" label={t('admin.types.form.nameEn')} />
      </div>
    </FormDialog>
  )
}
